<template>
    <div class="card" v-show="loaded">

        <div class="card-header border-0">
            <div class="row">
                <div class="col-6">
                    <h3 class="mb-0 text-uppercase">{{ $t('common.users') }}</h3>
                </div>
                <div class="col-6 text-right pr-0">

                    <base-button type="secondary" size="sm" @click="clearFilters" v-if="hasFilters">
                        {{ $t('common.clear-filters') }}
                    </base-button>

                    <base-button type="primary" size="sm" @click="ctrl.create()" v-if="fullRights || pilotRights">
                        <span class="btn-inner--icon"><i class="tim-icons icon-e-add"></i></span>
                        <span class="btn-inner--text">{{ $t('users.new') }}</span>
                    </base-button>

                    <actions-dropdown :actions="mainActions()" v-if="fullRights && canDeleteAll">
                    </actions-dropdown>
                </div>
            </div>
        </div>

        <div class="pt-2">
            
            <table-control :paging="ctrl.paging" :search="true" v-model="search" ref="search"></table-control>

            <el-table class="table-responsive table-flush"
                    header-row-class-name="thead-light"
                    :data="ctrl.rows"
                    @sort-change="sortChange"
                    @filter-change="filterChange"
                    ref="table">

                <el-table-column width="72">
                    <template v-slot="{row}">
                        <a href="#" class="avatar rounded-circle"
                                :class="{'disabled': !row.active}"
                                @click.prevent="open(row)">
                            <img :src="row.avatar">
                        </a>
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.name') + ' / ' + $t('common.email')"
                    sortable="custom" prop="lastname"
                 >
                    <template v-slot="{row}">
                        <div :class="{'disabled': !row.active}">
                            <h4 class="mb-0">
                                <a href="#" @click.prevent="open(row)">
                                    {{ row.name }}
                                </a>
                                <badge type="primary" class="mr-1" v-if="$auth.user.id === row.id">
                                    {{ $t('common.me') }}
                                </badge>
                            </h4>
                            <small>
                                <a :href="'mailto:'+row.email">
                                    {{ row.email }}
                                </a>
                            </small>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.role')" width="120"
                    column-key="role"
                    :filters="filtering.filteringValues(roles)"
                    :filtered-value="filters.role"
                    filter-placement="bottom-end"
                >
                    <template v-slot="{row}">
                        <div :class="{'disabled': !row.active}">
                            {{ roles[row.role_id] }}
                        </div>
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.client')" width="120"
                    column-key="entity"
                    :filters="filtering.filteringValues(entities, true)"
                    :filtered-value="filters.entity"
                    filter-placement="bottom-end"
                >
                    <template v-slot="{row}">
                        <div :class="{'disabled': !row.active}" v-if="row.entity_id">
                            {{ entities[row.entity_id] }}
                        </div>
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.owner')" width="120"
                    column-key="owner"
                    :filters="filtering.filteringValues(owners, true)"
                    :filtered-value="filters.owner"
                    filter-placement="bottom-end"
                >
                    <template v-slot="{row}">
                        <div :class="{'disabled': !row.active}" v-if="row.owner_id">
                            {{ owners[row.owner_id] }}
                        </div>
                    </template>
                </el-table-column>

                <el-table-column width="40"
                    column-key="source"
                    :filters="sources"
                    :filtered-value="filters.source"
                    filter-placement="bottom-end"
                >
                    <template v-slot="{row}">
                        <div :class="{'disabled': !row.active}" v-if="row.source == 'ldap' || row.source == 'ldap2'">
                            <i class="fas fa-plug"></i>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column align="right" width="130">
                    <template v-slot="{row}">

                        <a href="#" class="table-action text-danger" @click.prevent="ctrl.delete(row.id)"
                                v-if="row.id != $auth.user.id && (fullRights || pilotRights)">
                            <i class="tim-icons icon-simple-remove"></i>
                        </a>

                        <a href="#" class="table-action text-primary" @click.prevent="ctrl.update(row.id)">
                            <i class="tim-icons icon-pencil"></i>
                        </a>

                        <a href="#" class="table-action text-info" @click.prevent="open(row)">
                            <i class="tim-icons icon-badge-13"></i>
                        </a>

                    </template>
                </el-table-column>
                                
                <el-table-column align="right" width="50" class-name="actions">
                    <template v-slot="{row}">
                        <div class="table-actions">
                            <actions-dropdown :actions="actions(row)"></actions-dropdown>
                        </div>
                    </template>
                </el-table-column>

            </el-table>

            <table-pagination :paging="ctrl.paging"></table-pagination>
        </div>

        <portal to="modals">

            <form-modal v-model="ctrl.modals.edit"
                :title="$t('common.user')"
                :loading="ctrl.form.loading"
            >
                <user-form v-model="ctrl.form.data"
                    :errors="ctrl.form.errors"
                    :modal="ctrl.modals.edit"
                    :entities="entities"
                    :owners="owners"
                    :roles="roles"
                >
                </user-form>
            </form-modal>

            <confirm-modal v-model="ctrl.modals.confirmDelete">
                <p>{{ $t('users.delete-confirm') }}</p>
            </confirm-modal>

            <confirm-modal v-model="ctrl.modals.confirmForget">
                <p>{{ $t('users.forget-confirm') }}</p>
            </confirm-modal>

        </portal>

    </div>
</template>

<script>
    import CrudController from '@trax/classes/CrudController'
    import CrudFiltering from '../../../classes/CrudFiltering'
    import UserForm from './UserForm'
    import UserMapping from './UserMapping'

    export default {

        components: {
            UserForm
        },

        data() {
            let mapping = new UserMapping()
            let filtering = new CrudFiltering('crud_users')
            return {
                loaded: false,
                filtering,
                ctrl: new CrudController({
                    endpoint: '/astral/api/front/users',
                    mapData: mapping.mapData,
                    unmapData: mapping.unmapData,
                    modals: ['confirmForget'],
                    paging: {
                        perPage: 10,
                        perPageOptions: [5, 10, 25, 50],
                        total: 0
                    },
                    query: {
                        sort: ['lastname'],
                        include: ['roles', 'entities', 'owners']
                    },
                    errors: {
                        '403-delete': 'users.403-delete',
                    },
                    callbacks: {
                        listed: this.listed
                    }
                }),
                entities: {},
                owners: {},
                roles: {},
                search: '',
                filters: {},
            }
        },

        computed: {

            canDeleteAll() {
                return this.fullRights && this.$auth.hasPermission('admin.delete-personal-data')
            },

            fullRights() {
                return this.$auth.hasPermission('user.manage')
            },

            pilotRights() {
                return !this.fullRights && this.$auth.hasPermission('user.manage-pilots')
            },

            empty() {
                return this.ctrl.rows.length == 0 && !this.search
            },

            sources() {
                return [
                    {value: 'internal', text: this.$t('users.internal')},
                    {value: 'ldap', text: this.$t('users.ldap')},
                    {value: 'ldap2', text: this.$t('users.ldap2')},
                ]
            },

            hasFilters() {
                return Object.keys(this.filters).length > 0 || this.ctrl.query.sort[0] != 'lastname'
            }
        },

        created() {
            this.filtering.init(this)

            this.ctrl.init(this, 'ctrl').then(resp => {
                this.loaded = true
                this.entities = Array.isArray(resp.data.included.entities) ? {} : resp.data.included.entities
                this.owners = Array.isArray(resp.data.included.owners) ? {} : resp.data.included.owners
                this.roles = Array.isArray(resp.data.included.roles) ? {} : resp.data.included.roles
                if (this.fullRights && this.$auth.user.admin) {
                    this.roles[0] = this.$t('common.super-admin')
                }
            })
        },

        mounted() {
            if (this.filters.simpleSearch) {
                this.$refs.search.setFilter(this.filters.simpleSearch)
            }
        },

        watch: {
            search(val) {
                if (val === '') {
                    delete this.filters.simpleSearch
                } else {
                    this.filters.simpleSearch = val
                }
                this.filters = {...this.filters}    // Clone to force reactivity
                this.reload()
            }
        },

        methods: {

            sortChange(data) {
                this.filtering.sortChange(data)
            },

            filterChange(data) {
                this.filtering.filterChange(data)
            },

            clearFilters() {
                this.ctrl.query.sort = ['lastname']
                this.filtering.clearFilters()
            },

            listed() {
                this.filtering.saveUiConfig()
            },

            requestFilters() {
                let filters = {$and: []}
                
                // Role.
                if (this.filters.role) {
                    let inRole = {$or: [
                        {role_id: {$in: this.filters.role.map(id => parseInt(id)).filter(val => val) }}
                    ]}
                    if (this.filters.role.includes('0')) {
                        inRole.$or.push({admin: true})
                    }
                    filters.$and.push(inRole)
                }

                // Entity.
                if (this.filters.entity) {
                    let inEntity = {$or: [
                        {entity_id: {$in: this.filters.entity.map(id => parseInt(id)).filter(val => val) }}
                    ]}
                    if (this.filters.entity.includes('x')) {
                        inEntity.$or.push({entity_id: {$exists: false}})
                    }
                    filters.$and.push(inEntity)
                }

                // Owner.
                if (this.filters.owner) {
                    let inOwner = {$or: [
                        {owner_id: {$in: this.filters.owner.map(id => parseInt(id)).filter(val => val) }}
                    ]}
                    if (this.filters.owner.includes('x')) {
                        inOwner.$or.push({owner_id: {$exists: false}})
                    }
                    filters.$and.push(inOwner)
                }

                // Source.
                if (this.filters.source) {
                    let inSource = {source: {$in: this.filters.source }}
                    filters.$and.push(inSource)
                }

                // Search.
                if (this.filters.simpleSearch) {
                    filters.$and.push({ simpleSearch: this.filters.simpleSearch})
                }

                if (filters.$and.length == 0) {
                    filters = {}
                }
                
                return filters
            },

            reload() {
                this.ctrl.filter(this.requestFilters())
            },

            canForgetUser(row) {
                return row.id != this.$auth.user.id && this.$auth.hasPermission('admin.delete-personal-data')
            },
                        
            mainActions() {
                let actions = [{
                    label: this.$t('common.import'),
                    handler: this.importUsers,
                }]
                if (this.canDeleteAll) {
                    actions.push({
                        label: this.$t('users.outdated'),
                        handler: this.deleteOutdated,
                    })
                }
                return actions
            },

            importUsers() {
                this.$router.push({ name: 'import-users'})
            },

            deleteOutdated() {
                this.$router.push({ name: 'clear-users'})
            },

            actions(row) {
                let actions = []
                if (this.canForgetUser(row)) {
                    actions.push({
                        label: this.$t('users.forget'),
                        handler: this.forgetUser,
                        data: row
                    })
                }
                return actions
            },

            forgetUser(row) {
                this.ctrl.modals.open('confirmForget').then(confirmResponse => {
                    if (confirmResponse.confirmed) {
                        axios.post('/astral/api/front/users/'+row.id+'/forget')
                        .then(resp => {
                            this.ctrl.list()
                        })
                        .catch(err => {
                            if (err.response.status == 423) {
                                this.$notify({type: 'danger', message: this.$t('users.423-forget') })
                            } else {
                                this.$auth.handleErrorVm(err, this)
                            }
                        })        
                    }
                })
            },

            open(row) {
                this.$router.push({ name: 'admin-user', params: { id: row.id } })
            }
        }
    }
</script>

<style scoped>

    .disabled, .disabled a {
        color: #bbb !important;
    }
    .disabled img {
        opacity: 0.5;
    }
    .card >>> td,
    .card >>> th {
        padding-right: 0;
    }
    .card >>> td.actions,
    .card >>> th.actions {
        padding-left: 0;
        padding-right: 1.5rem;
    }

</style>
